import { useCallback, useEffect, useRef, useState } from 'react';

import { getSelectedStoreCurrentTimeUTC } from 'features/store/utils';
import { noop } from 'lodash-es';

import { IServerOrder } from '@rbi-ctg/menu';
import {
  GetOrderDocument,
  GetUserOrdersDocument,
  useCommitOrderMutation,
} from 'generated/graphql-gateway';
import { useOrderStatus } from 'hooks/order-status';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useCartContext } from 'state/cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { useOrderContext } from 'state/order';
import { getServiceModeFromLS } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { buildCommitDeliveryInput } from 'utils/cart/build-commit-delivery-input';
import { buildCommitOrderInput } from 'utils/cart/build-commit-order-input';

import { ICommitOrderInput, IUseCommitOrderParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

export const useCommitOrder = ({
  onCompleted = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
  distanceToStore,
  checkoutData,
}: IUseCommitOrderParams) => {
  const [skipPolling, setSkipPolling] = useState(true);
  const { tipAmount: tipAmountInCart } = useCartContext();
  const {
    onCommitSuccess,
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    pickupDate,
  } = useOrderContext();
  const fireOrderAheadSeconds = useFlag(LaunchDarklyFlag.FIRE_ORDER_AHEAD) || 0;

  const { user } = useAuthContext();
  const commitOptions = useRef({} as ICommitOrderInput);

  const [fireMutation, mutationResult] = useCommitOrderMutation({
    fetchPolicy: 'network-only',
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GetOrderDocument, variables: { id: rbiOrderId } },
      { query: GetUserOrdersDocument },
    ],
  });

  useEffect(() => {
    if (mutationResult.called && !mutationResult.loading) {
      let attributes;
      if (mutationResult.error) {
        attributes = {
          status: 'Failed',
          failureDetails: mutationResult.error.message,
        };
      } else {
        attributes = {
          status: mutationResult.data?.commitOrder.status || '',
        };
      }

      logRBIEvent(
        {
          name: CustomEventNames.SUBMIT_PAYMENT,
          type: EventTypes.Other,
          attributes,
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [mutationResult.called, mutationResult.loading, mutationResult.data, mutationResult.error]);

  const onSuccess = useCallback(
    (rbiOrder: IServerOrder) => {
      onCompleted();
      onCommitSuccess(rbiOrder);
    },
    [onCommitSuccess, onCompleted]
  );

  const queryResult = useOrderStatus({
    onSuccess,
    orderStatusPollInterval,
    rbiOrderId,
    skip: skipPolling || mutationResult.loading,
  });

  const { orderStatus, serverOrder } = queryResult;

  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const isOrderTimedFire = orderInjectFlow === OrderInjectionFlowVariations.TIMED_FIRE;
  const isOrderInjectAndHold = orderInjectFlow === OrderInjectionFlowVariations.INJECT_AND_HOLD;

  const phoneNumber = orderPhoneNumber || user?.details.phoneNumber || '';
  const commitOrder = useCallback(
    async (options: ICommitOrderInput) => {
      commitOptions.current = options;
      const serviceMode = getServiceModeFromLS();
      const isDelivery = serviceMode === ServiceMode.DELIVERY;
      const isDriveThru = serviceMode === ServiceMode.DRIVE_THRU;
      // TODO: remove tipAmountInCart once we complete the experiment and have a way to get the tip amount from the cart context
      // https://rbictg.atlassian.net/browse/BKPE-15723
      const tipAmount = options.deliveryTipCents ?? tipAmountInCart;
      const delivery = buildCommitDeliveryInput({
        deliveryAddress,
        deliveryInstructions,
        phoneNumber,
        isDelivery,
        tipAmount,
      });

      let fireOrderIn: number | null = 0;

      if (pickupDate) {
        const storeDate = getSelectedStoreCurrentTimeUTC();
        const aheadSeconds =
          (pickupDate.getTime() - storeDate.getTime()) / 1000 - fireOrderAheadSeconds;
        fireOrderIn = aheadSeconds <= 0 ? 0 : Math.round(aheadSeconds);
      }

      // for backwards compatibility purposes, we need to convert 0 to 1 for ASAP drive thru orders
      if (isDriveThru && isOrderTimedFire && fireOrderIn === 0) {
        fireOrderIn = 1;
      }

      if (isOrderInjectAndHold) {
        fireOrderIn = null;
      }

      const input = buildCommitOrderInput({
        ...options,
        fireOrderIn,
        rbiOrderId: rbiOrderId!,
        distanceToStore,
        checkoutData,
        pushNotification: true,
      });

      const skipCoolingPeriod = !!options.skipCoolingPeriod;
      const result = await fireMutation({
        variables: {
          delivery,
          input,
          skipCoolingPeriod,
        },
      });
      if (result.data) {
        setSkipPolling(false);
      }
      return result;
    },
    [
      tipAmountInCart,
      deliveryAddress,
      deliveryInstructions,
      phoneNumber,
      pickupDate,
      isOrderTimedFire,
      isOrderInjectAndHold,
      rbiOrderId,
      distanceToStore,
      checkoutData,
      fireMutation,
      fireOrderAheadSeconds,
    ]
  );

  const forceCommitOrder = useCallback(() => {
    return commitOrder({ ...commitOptions.current, skipCoolingPeriod: true });
  }, [commitOrder]);

  return {
    commitOrder,
    commitResult: mutationResult,
    failure: queryResult.failure,
    forceCommitOrder,
    loading: mutationResult.loading || queryResult.isLoading,
    order: queryResult,
    orderStatus,
    serverOrder,
    success: queryResult.success,
  };
};
